<p-contextmenu #cm [model]="taskCrud?.contextMenu" appendTo="body" />

<p-treetable
    #ptree
    stripedRows
    [rowTrackBy]="TreeUtils.treeNodeTrackByKey"
    [dataKey]="'uuid'"
    [value]="treeItems"
    [loading]="taskCrud?.showLoading ?? true"
    [resizableColumns]="false"
    [autoLayout]="true"
    [scrollable]="true"
    [scrollHeight]="'flex'"
    styleClass="p-treetable-striped p-treetable-gridlines p-treetable-sm gantt-treetable"
    [resizableColumns]="false"
    columnResizeMode="expand"
    [reorderableColumns]="false"
    [selectionMode]="'single'"
    (selectionChange)="selectionChange($event)"
    [(selection)]="selectedTask"
    (onContextMenuSelect)="contextMenuSelect($event)"
    [contextMenu]="cm"
>
    <ng-template pTemplate="caption">
        <div class="header-flex">
            <div style="display: flex; gap: 0.5rem">
                <div style="width: 20rem; display: inline-block">
                    <p-iconfield>
                        <p-inputicon class="pi pi-search" />
                        <input
                            pInputText
                            type="text"
                            [value]="_searchValue ?? ''"
                            (input)="ptree.filterGlobal($any($event.target).value, 'contains')"
                            placeholder="Search words"
                        />
                    </p-iconfield>
                </div>
            </div>

            <div class="caption-controls">
                <div class="caption-buttons">
                    <p-button
                        icon="pi pi-chevron-left"
                        severity="secondary"
                        [outlined]="true"
                        size="small"
                        [rounded]="true"
                        (click)="navigate(-1)"
                        pTooltip="Previous week"
                    />
                    <p-button
                        [label]="startDateLabel"
                        severity="secondary"
                        [outlined]="true"
                        size="small"
                        (click)="goToToday()"
                    />
                    <p-button
                        icon="pi pi-chevron-right"
                        severity="secondary"
                        [outlined]="true"
                        size="small"
                        [rounded]="true"
                        (click)="navigate(1)"
                        pTooltip="Next week"
                    />
                    <p-button severity="secondary" icon="pi pi-refresh" (click)="taskCrud?.refresh()" />
                </div>
            </div>
        </div>
    </ng-template>

    <ng-template pTemplate="loadingicon">
        <p-progress-spinner styleClass="w-4rem h-4rem" strokeWidth="4" animationDuration="1.5s"></p-progress-spinner>
        <div class="mt-2 mb-2" [ngStyle]="{color: 'white', fontSize: '1.5rem', fontWeight: 'bold'}">
            {{ taskCrud?.loadingStr ?? 'Loading...' }}
        </div>
    </ng-template>

    <ng-template pTemplate="emptymessage">
        <tr>
            <td colspan="50">
                @if (treeItems.length === 0 && !taskCrud?.showError) {
                    <p-message severity="warn">There is nothing to show.</p-message>
                }
                @if (taskCrud?.showError) {
                    <p-message severity="warn">An error occured while getting datas.</p-message>
                }
            </td>
        </tr>
    </ng-template>

    <ng-template #colgroup let-columns>
        <colgroup>
            <col />
            <col />
            <col />
            <col />
            @for (day of dayColumns; track day.date.getTime()) {
                <col />
            }
        </colgroup>
    </ng-template>

    <ng-template pTemplate="header">
        <tr>
            <th class="gantt-task-name-col" [attr.rowspan]="2">Tasks</th>
            <th class="gantt-date-col" [attr.rowspan]="2">Start</th>
            <th class="gantt-date-col" [attr.rowspan]="2">End</th>
            <th class="gantt-date-col last" [attr.rowspan]="2">Due</th>
            @for (week of weekGroups; track week.label) {
                <th
                    class="gantt-week-header"
                    [attr.colspan]="week.colspan"
                    [style.width]="'calc(66% / ' + weeksToShow + ')'"
                >
                    {{ week.label }}
                </th>
            }
        </tr>
        <tr>
            @for (day of dayColumns; track day.date.getTime()) {
                <th
                    class="gantt-day-header"
                    [class.gantt-weekend]="day.isWeekend"
                    [class.gantt-today]="day.isToday"
                    [style.width]="'calc(66% / (' + weeksToShow + ' * 7))'"
                >
                    <span>{{ day.dayLetter }}</span>
                    <span>{{ day.date.getDate() }}</span>
                </th>
            }
        </tr>
    </ng-template>

    <ng-template pTemplate="body" let-rowNode let-rowData="rowData">
        <tr
            [ttRow]="rowNode"
            [ttSelectableRow]="rowNode"
            [ttContextMenuRow]="rowNode"
            [class.gantt-row-selected]="isSelectedTask(rowData)"
        >
            <td class="gantt-task-name-col">
                <div [ngStyle]="rowNode.node.style" [pTooltip]="rowData.name">
                    <p-treetable-toggler [rowNode]="rowNode" />
                    {{ rowData.name }}
                    @if (rowNode.node.icon) {
                        <span [ngClass]="rowNode.node.icon" [ngStyle]="rowNode.node.iconStyle"></span>
                    }
                </div>
            </td>

            <td class="gantt-date-col gantt-date-editable" (click)="onDateCellClick(rowData, 'start_date')">
                {{ (rowData.start_date | date: 'dd MMM') ?? '-' }}
            </td>
            <td class="gantt-date-col gantt-date-editable" (click)="onDateCellClick(rowData, 'end_date')">
                {{ (rowData.end_date | date: 'dd MMM') ?? '-' }}
            </td>
            <td class="gantt-date-col last gantt-date-editable" (click)="onDateCellClick(rowData, 'due_date')">
                {{ (rowData.due_date | date: 'dd MMM') ?? '-' }}
            </td>

            <!-- Gantt day cells -->
            @for (day of dayColumns; track day.date.getTime()) {
                <td
                    class="gantt-cell"
                    [class.gantt-weekend]="day.isWeekend"
                    [class.gantt-today]="day.isToday"
                    [class.gantt-active]="isTaskActiveOnDay(rowData, day)"
                    [class.gantt-bar-start]="isBarStart(rowData, day)"
                    [class.gantt-bar-end]="isBarEnd(rowData, day)"
                    [class.gantt-due-date]="isDueDate(rowData, day)"
                    [class.gantt-dragging]="dragState && dragState.dragging && dragState.task === rowData"
                    [class.gantt-assignable]="isAssignableCell(rowData)"
                    [ngClass]="isTaskActiveOnDay(rowData, day) ? getBarColorClass(rowData) : ''"
                    [style.width]="'calc(66% / (' + weeksToShow + ' * 7))'"
                    (mousemove)="onCellMouseMove(rowData, day, $event)"
                    (mousedown)="onCellMouseDown(rowData, day, $event)"
                    (mouseup)="onCellMouseUp(rowData, day)"
                    (click)="$event.stopPropagation()"
                >
                    @if (isBarStart(rowData, day) && isTaskActiveOnDay(rowData, day)) {
                        <span class="gantt-bar-date-label gantt-bar-date-start">{{
                            getBarDateLabel(rowData, day)
                        }}</span>
                    }
                    @if (isBarEnd(rowData, day) && isTaskActiveOnDay(rowData, day) && !isBarStart(rowData, day)) {
                        <span class="gantt-bar-date-label gantt-bar-date-end">{{ getBarDateLabel(rowData, day) }}</span>
                    }
                    @if (isDueDate(rowData, day)) {
                        <span class="gantt-due-marker"></span>
                    }
                    @if (day.isToday && !isTaskActiveOnDay(rowData, day) && !isDueDate(rowData, day)) {
                        <span class="gantt-today-marker"></span>
                    }
                </td>
            }
        </tr>
    </ng-template>
</p-treetable>
